import { first, sumBy } from 'lodash-es';
import { DateTime } from 'luxon';
import { ProductName, RatePlan, Subscription } from './types';

type SubscriptionStatus = 'active' | 'canceled' | 'expired' | 'altered';

export interface SubscriptionModel {
  id: string;
  termEndDate?: string;
  renewalDate?: string;
  subscriptionNumber: string;
  status: SubscriptionStatus;
  licenseKey: string;
  licenseType: ProductName;
  seats: number;
  alteredSeats: number;
  serviceActivationDate: DateTime;
  invoiceOwnerAccountId: string;
}

const displayableProductNames = ['Fixed', 'Floating'];

const isRatePlanDisplayable = (ratePlan: RatePlan): boolean =>
  displayableProductNames.includes(ratePlan.productName) && ratePlan.lastChangeType !== 'Removed';

const getSeats = (ratePlans: RatePlan[]): number =>
  ratePlans.reduce((acc, { ratePlanCharges }) => {
    return acc + sumBy(ratePlanCharges, 'quantity');
  }, 0);

const getStatus = (
  alteredSeats: number,
  originalSubscription: Subscription,
): SubscriptionStatus => {
  if (originalSubscription.status === 'Active') {
    return alteredSeats === 0 ? 'active' : 'altered';
  }
  if (DateTime.now().diff(DateTime.fromISO(originalSubscription.termEndDate), 'day').days >= 0) {
    return 'expired';
  }

  return 'canceled';
};

export const createSubscriptionModel = (originalSubscription: Subscription): SubscriptionModel => {
  const displayableRatePlans = originalSubscription.ratePlans.filter(isRatePlanDisplayable);
  const displayableLatestRatePlans =
    originalSubscription.latestRateplans.filter(isRatePlanDisplayable);
  const termEndDate =
    originalSubscription.status === 'Cancelled' ? originalSubscription.termEndDate : undefined;
  const renewalDate =
    originalSubscription.status === 'Active' ? originalSubscription.termEndDate : undefined;
  const licenseType = first(displayableRatePlans)?.ratePlanName as ProductName;
  const currentSeats = getSeats(displayableRatePlans);
  const nextSeats = getSeats(displayableLatestRatePlans);
  const alteration = nextSeats - currentSeats;
  const status = getStatus(alteration, originalSubscription);

  return {
    id: originalSubscription.id,
    licenseKey: originalSubscription.notes,
    subscriptionNumber: originalSubscription.subscriptionNumber,
    serviceActivationDate: DateTime.fromISO(originalSubscription.serviceActivationDate),
    renewalDate,
    termEndDate,
    licenseType,
    status,
    seats: currentSeats,
    alteredSeats: alteration,
    invoiceOwnerAccountId: originalSubscription.invoiceOwnerAccountId,
  };
};
